import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Éditeur plein écran pour le terminal du lab : `nano` (simple) et `vim`/`vi`
 * (modal : NORMAL / INSERT / commande `:`). Reproduit l'usage réel à l'examen
 * (ouvrir un manifeste, éditer, sauver) sans viser une émulation vim complète.
 */
export interface LabEditorProps {
  file: string;
  initialContent: string;
  mode: 'vim' | 'nano';
  /** Écrit le contenu dans le VFS (`:w`, `:wq`, Ctrl+O). */
  onWrite: (content: string) => void;
  /** Ferme l'éditeur et rend la main au terminal (avec un message optionnel). */
  onExit: (message?: string) => void;
}

type VimMode = 'normal' | 'insert' | 'command';

export function LabEditor({ file, initialContent, mode, onWrite, onExit }: LabEditorProps) {
  const [text, setText] = useState(initialContent);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const [vimMode, setVimMode] = useState<VimMode>('normal');
  const [cmdline, setCmdline] = useState('');
  const [confirmExit, setConfirmExit] = useState(false); // nano : sauver avant de quitter ?
  const taRef = useRef<HTMLTextAreaElement>(null);
  const cmdRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<string>(''); // séquences vim (ex. « dd »)

  const lineCount = text.split('\n').length;

  useEffect(() => {
    // Focus initial : textarea (nano/insert), sinon on garde le focus clavier
    // sur le conteneur pour capter les touches NORMAL.
    if (mode === 'nano') taRef.current?.focus();
    else taRef.current?.focus();
  }, [mode]);

  const update = (v: string) => { setText(v); setDirty(true); };

  const write = useCallback(() => {
    onWrite(text);
    setDirty(false);
    setStatus(`"${file}" ${lineCount}L écrit`);
  }, [onWrite, text, file, lineCount]);

  // ── Curseur : helpers sur le textarea ──
  const ta = () => taRef.current;
  const setCaret = (pos: number) => {
    const el = ta(); if (!el) return;
    const p = Math.max(0, Math.min(text.length, pos));
    el.selectionStart = el.selectionEnd = p;
  };
  const caret = () => ta()?.selectionStart ?? 0;
  const lineBounds = (pos: number) => {
    const start = text.lastIndexOf('\n', pos - 1) + 1;
    let end = text.indexOf('\n', pos);
    if (end === -1) end = text.length;
    return { start, end };
  };

  // ── VIM : commandes « : » ──
  const runVimCommand = (raw: string) => {
    const c = raw.trim();
    setCmdline('');
    setVimMode('normal');
    if (c === 'w') { write(); return; }
    if (c === 'wq' || c === 'x' || c === 'wq!') { onWrite(text); onExit(`"${file}" écrit`); return; }
    if (c === 'q') { if (dirty) { setStatus('E37: Modifications non sauvegardées (:q! pour forcer)'); } else onExit(); return; }
    if (c === 'q!') { onExit(); return; }
    setStatus(`E492: Commande non reconnue : ${c}`);
  };

  // ── VIM : touches en mode NORMAL ──
  const handleVimNormal = (e: React.KeyboardEvent) => {
    const k = e.key;
    // Séquence « dd » : supprime la ligne courante.
    if (pendingRef.current === 'd') {
      pendingRef.current = '';
      if (k === 'd') {
        e.preventDefault();
        const { start, end } = lineBounds(caret());
        const delEnd = end < text.length ? end + 1 : end;
        update(text.slice(0, start) + text.slice(delEnd));
        setTimeout(() => setCaret(start), 0);
        return;
      }
    }
    if (k === 'Escape') { e.preventDefault(); return; }
    if (k === ':') { e.preventDefault(); setVimMode('command'); setCmdline(''); setTimeout(() => cmdRef.current?.focus(), 0); return; }
    if (k === 'i') { e.preventDefault(); setVimMode('insert'); return; }
    if (k === 'a') { e.preventDefault(); setCaret(caret() + 1); setVimMode('insert'); return; }
    if (k === 'A') { e.preventDefault(); setCaret(lineBounds(caret()).end); setVimMode('insert'); return; }
    if (k === 'I') { e.preventDefault(); setCaret(lineBounds(caret()).start); setVimMode('insert'); return; }
    if (k === 'o') {
      e.preventDefault();
      const { end } = lineBounds(caret());
      update(text.slice(0, end) + '\n' + text.slice(end));
      setTimeout(() => { setCaret(end + 1); setVimMode('insert'); }, 0);
      return;
    }
    if (k === 'O') {
      e.preventDefault();
      const { start } = lineBounds(caret());
      update(text.slice(0, start) + '\n' + text.slice(start));
      setTimeout(() => { setCaret(start); setVimMode('insert'); }, 0);
      return;
    }
    if (k === 'x') {
      e.preventDefault();
      const p = caret();
      if (p < text.length && text[p] !== '\n') { update(text.slice(0, p) + text.slice(p + 1)); setTimeout(() => setCaret(p), 0); }
      return;
    }
    if (k === 'd') { e.preventDefault(); pendingRef.current = 'd'; return; }
    // Navigation hjkl (les flèches marchent nativement aussi).
    if (k === 'h') { e.preventDefault(); setCaret(caret() - 1); return; }
    if (k === 'l') { e.preventDefault(); setCaret(caret() + 1); return; }
    if (k === 'j' || k === 'k') {
      e.preventDefault();
      const { start, end } = lineBounds(caret());
      const col = caret() - start;
      if (k === 'j' && end < text.length) { const nb = lineBounds(end + 1); setCaret(Math.min(nb.start + col, nb.end)); }
      else if (k === 'k' && start > 0) { const pb = lineBounds(start - 1); setCaret(Math.min(pb.start + col, pb.end)); }
      return;
    }
    // Toute autre touche imprimable est ignorée en NORMAL (pas de saisie).
    if (k.length === 1) e.preventDefault();
  };

  const handleVimInsert = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); setVimMode('normal'); ta()?.focus(); }
    // Sinon : saisie native dans le textarea.
  };

  // ── NANO : Ctrl+O (écrire) / Ctrl+X (quitter) ──
  const handleNanoKey = (e: React.KeyboardEvent) => {
    if (confirmExit) {
      const k = e.key.toLowerCase();
      if (k === 'y') { e.preventDefault(); onWrite(text); onExit(`"${file}" écrit`); }
      else if (k === 'n') { e.preventDefault(); onExit(); }
      else if (k === 'escape') { e.preventDefault(); setConfirmExit(false); }
      return;
    }
    if (e.ctrlKey && (e.key === 'o' || e.key === 'O')) { e.preventDefault(); write(); return; }
    if (e.ctrlKey && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      if (dirty) setConfirmExit(true); else onExit();
      return;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (mode === 'nano') { handleNanoKey(e); return; }
    if (vimMode === 'normal') handleVimNormal(e);
    else if (vimMode === 'insert') handleVimInsert(e);
    // command mode : géré par l'input dédié.
  };

  const editable = mode === 'nano' || vimMode === 'insert';

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#101418] text-slate-100 font-mono text-[13px]">
      {/* En-tête */}
      <div className="flex-shrink-0 px-4 py-1.5 bg-[#1b232b] text-slate-300 flex items-center justify-between border-b border-slate-700/60">
        {mode === 'nano' ? (
          <>
            <span className="font-semibold">GNU nano 6.2</span>
            <span className="text-slate-400">Fichier : {file}{dirty ? ' *' : ''}</span>
          </>
        ) : (
          <>
            <span className="font-semibold text-emerald-300">VIM — {file}</span>
            <span className="text-slate-400">{editable ? 'éditable' : 'lecture (NORMAL)'}{dirty ? ' [+]' : ''}</span>
          </>
        )}
      </div>

      {/* Zone d'édition */}
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => update(e.target.value)}
        onKeyDown={onKeyDown}
        readOnly={!editable}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className="flex-1 w-full resize-none bg-[#101418] text-slate-100 px-4 py-3 outline-none leading-relaxed caret-emerald-300"
        style={{ tabSize: 2 }}
      />

      {/* Barre de statut */}
      <div className="flex-shrink-0">
        {mode === 'vim' && vimMode === 'insert' && (
          <div className="px-4 py-1 bg-[#101418] text-emerald-300 font-bold">-- INSERT --</div>
        )}
        {mode === 'vim' && vimMode === 'command' && (
          <div className="px-4 py-1 bg-[#101418] flex items-center">
            <span className="text-slate-100">:</span>
            <input
              ref={cmdRef}
              value={cmdline}
              onChange={(e) => setCmdline(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); runVimCommand(cmdline); ta()?.focus(); }
                else if (e.key === 'Escape') { e.preventDefault(); setCmdline(''); setVimMode('normal'); ta()?.focus(); }
              }}
              className="flex-1 bg-transparent outline-none text-slate-100"
              autoFocus
            />
          </div>
        )}
        {mode === 'vim' && vimMode === 'normal' && (
          <div className="px-4 py-1 bg-[#101418] text-slate-400 text-xs">
            {status || 'NORMAL — i insérer · o nouvelle ligne · x supprimer · dd supprimer ligne · :wq sauver & quitter · :q! quitter'}
          </div>
        )}
        {mode === 'nano' && (
          <div className="bg-[#1b232b] text-slate-300 text-xs">
            {confirmExit ? (
              <div className="px-4 py-2 text-amber-300">Sauvegarder le tampon modifié ? (O = oui via Y, N = non)</div>
            ) : (
              <>
                {status && <div className="px-4 py-1 text-emerald-300">{status}</div>}
                <div className="px-4 py-2 flex flex-wrap gap-x-6 gap-y-1">
                  <span><b className="text-white">^O</b> Écrire</span>
                  <span><b className="text-white">^X</b> Quitter</span>
                  <span className="text-slate-500">(Ctrl+O pour sauver, Ctrl+X pour quitter)</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
